import React, {
  useMemo,
  useState,
  useEffect,
  useCallback,
  useRef,
} from "react";
import {
  useReactTable,
  getCoreRowModel,
  getSortedRowModel,
  getPaginationRowModel,
  ColumnDef,
  ColumnSizingState,
} from "@tanstack/react-table";
import { TableProps } from "./types";
import { TableContainer, TableWrapper, StyledTable } from "./styles";
import { Header } from "./Header";
import { TableBody } from "./TableBody";
import { Pagination } from "./Pagination";
import { ActionBar } from "./ActionBar";
import { BulkSelectionBar } from "./BulkSelectionBar";
import { SearchBar } from "./SearchBar";

/**
 * Normalize columns to support both react-table v7 and TanStack Table v8 formats
 */
function normalizeColumns<T extends object>(
  columns: any[]
): ColumnDef<T, any>[] {
  return columns.map((col) => {
    const normalized: any = {
      ...col,
      header: col.header ?? col.Header ?? col.id,
      accessorKey: col.accessorKey ?? col.accessor ?? col.id,
      minSize: col.minSize ?? 60,
    };

    // If there's a Cell function from v7, convert to cell
    if (col.Cell && !normalized.cell) {
      normalized.cell = col.Cell;
    }

    // Keep sorting configuration
    if (col.disableSortBy !== undefined) {
      normalized.enableSorting = !col.disableSortBy;
    }

    return normalized;
  });
}

/**
 * CustomTable component
 * Replaces @tyrannosaurustech/ui Table component with identical API
 */
export function CustomTable<DataType extends object>({
  data,
  columns,
  initialTableState,
  actionButtonsConfig,
  actionRightButtons,
  actionRightAccessories,
  selectConfig,
  loading,
  asyncConfig,
  handleRowClick,
  emptyTableContent,
  resizableColumns,
  columnSizing: columnSizingProp,
  onColumnSizingChange,
}: TableProps<DataType>) {
  // Normalize columns to TanStack Table v8 format
  const normalizedColumns = useMemo(() => {
    const cols = normalizeColumns<DataType>(columns);

    // Add selection column if selectConfig is provided
    if (selectConfig) {
      const selectionColumn: ColumnDef<DataType, any> = {
        id: "select",
        header: ({ table }) => (
          <input
            type="checkbox"
            checked={table.getIsAllRowsSelected()}
            ref={(el) => {
              if (el) {
                el.indeterminate = table.getIsSomeRowsSelected();
              }
            }}
            onChange={table.getToggleAllRowsSelectedHandler()}
          />
        ),
        cell: ({ row }) => (
          <input
            type="checkbox"
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onChange={row.getToggleSelectedHandler()}
          />
        ),
        size: 48,
        enableSorting: false,
      };
      return [selectionColumn, ...cols];
    }

    return cols;
  }, [columns, selectConfig]);

  // Manage column sizing state
  const [columnSizing, setColumnSizing] = useState<ColumnSizingState>(
    columnSizingProp ?? {}
  );

  const handleColumnSizingChange: React.Dispatch<
    React.SetStateAction<ColumnSizingState>
  > = useCallback(
    (updater) => {
      setColumnSizing((prev) => {
        const next = typeof updater === "function" ? updater(prev) : updater;
        onColumnSizingChange?.(next);
        return next;
      });
    },
    [onColumnSizingChange]
  );

  // Manage row selection state locally
  const [rowSelection, setRowSelection] = useState<any>({});
  const prevSelectionRef = useRef<string>("");

  // Initialize table instance
  const table = useReactTable<DataType>({
    data: data || [],
    columns: normalizedColumns,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    // Manual pagination and sorting for async config
    manualPagination: !!asyncConfig,
    manualSorting: !!asyncConfig,
    // Pagination state
    pageCount: asyncConfig?.pageCount ?? -1,
    state: {
      pagination: asyncConfig
        ? {
            pageIndex: asyncConfig.pageIndex,
            pageSize: asyncConfig.pageSize,
          }
        : initialTableState?.pagination || {
            pageIndex: 0,
            pageSize: 50,
          },
      sorting: asyncConfig?.sort || [],
      ...(selectConfig ? { rowSelection } : {}),
      ...(resizableColumns ? { columnSizing } : {}),
    },
    // Callbacks for async config
    onPaginationChange: asyncConfig?.onPaginationChange,
    onSortingChange: asyncConfig?.onSortingChange,
    onRowSelectionChange: selectConfig ? setRowSelection : undefined,
    ...(resizableColumns
      ? { onColumnSizingChange: handleColumnSizingChange }
      : {}),
    // Enable row selection
    enableRowSelection: !!selectConfig,
    // Enable column resizing
    ...(resizableColumns
      ? { columnResizeMode: "onChange" as const, enableColumnResizing: true }
      : {}),
  });

  // Notify parent of selection changes (now that table is initialized)
  useEffect(() => {
    if (
      selectConfig?.handleRowsSelect &&
      Object.keys(rowSelection).length >= 0
    ) {
      const selectionStr = JSON.stringify(Object.keys(rowSelection).sort());
      if (prevSelectionRef.current !== selectionStr) {
        prevSelectionRef.current = selectionStr;
        const selectedRows = table.getSelectedRowModel().rows;
        selectConfig.handleRowsSelect(selectedRows);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowSelection]);

  // Clear selection function
  const clearSelection = useCallback(() => {
    setRowSelection({});
  }, []);

  // Expose clearSelection to parent via callback
  useEffect(() => {
    if (selectConfig?.onClearSelectionReady) {
      selectConfig.onClearSelectionReady(clearSelection);
    }
  }, [selectConfig, clearSelection]);

  // Get selection info
  const selectedRows = table.getSelectedRowModel().rows;
  const selectedCount = Object.keys(rowSelection).length;
  const bulkButtons = selectConfig?.bulkButtons
    ? selectConfig.bulkButtons({ rows: selectedRows })
    : [];

  const rows = table.getRowModel().rows;
  const headerGroups = table.getHeaderGroups();
  const columnCount = normalizedColumns.length;

  // Handle search
  const handleSearch = useCallback(
    (searchValue: string) => {
      if (asyncConfig?.onSearch) {
        asyncConfig.onSearch(searchValue, []);
      }
    },
    [asyncConfig]
  );

  return (
    <TableContainer className="inside-table">
      {/* Search bar */}
      {asyncConfig && (
        <SearchBar
          value={asyncConfig.facets?.search || ""}
          onChange={handleSearch}
          placeholder="Search"
        />
      )}

      {/* Action buttons bar */}
      <ActionBar
        actionButtonsConfig={actionButtonsConfig}
        actionRightButtons={actionRightButtons}
        actionRightAccessories={actionRightAccessories}
      />

      {/* Bulk selection bar (shown when rows are selected) */}
      {selectConfig && (
        <BulkSelectionBar
          selectedCount={selectedCount}
          bulkButtons={bulkButtons}
          clearSelection={clearSelection}
          selectedRows={selectedRows}
        />
      )}

      {/* Table */}
      <TableWrapper>
        <StyledTable
          $fixedLayout={resizableColumns}
          ref={
            resizableColumns
              ? (el) => {
                  if (!el) return;
                  const update = () =>
                    el.style.setProperty(
                      "--table-height",
                      `${el.scrollHeight}px`
                    );
                  update();
                  if (!(el as any).__resizeObserver) {
                    const ro = new ResizeObserver(update);
                    ro.observe(el);
                    (el as any).__resizeObserver = ro;
                  }
                }
              : undefined
          }
        >
          <Header
            headerGroups={headerGroups}
            table={table}
            resizable={resizableColumns}
          />
          <TableBody
            rows={rows}
            loading={loading}
            emptyContent={emptyTableContent}
            columnCount={columnCount}
            handleRowClick={handleRowClick}
          />
        </StyledTable>
      </TableWrapper>

      {/* Pagination */}
      {(asyncConfig || rows.length > 0) && <Pagination table={table} />}
    </TableContainer>
  );
}

// Export types and hooks for external use
export { useAsyncConfig } from "./useAsyncConfig";
export type { PaginatedApi, PaginatedApiResponse } from "./useAsyncConfig";
export type { TableProps, AsyncConfig, SelectConfig } from "./types";
